import { useTranslation } from "react-i18next";

import moment from "moment-timezone";

import { COMPANY_CARD_EXPIRY_STATUS } from "../../../../../../shared";
import { BadgeColors } from "../../../../../Badge/types";

export const useCompanyCardDetails = () => {
  const { t } = useTranslation();

  const getExpiryLabel = (rawDate: string): string => {
    const now = moment.utc().startOf("day");
    const expiryDate = moment.utc(rawDate, "YYYY-MM-DD").startOf("day");

    // strings are not translated and are kept as magic strings due to the dynamic nature of the expiry label
    // translations could be added later if needed

    if (now.isAfter(expiryDate)) {
      return t("General##Expired");
    }

    const totalMonthsFloat = expiryDate.diff(now, "months", true);
    const totalDays = Math.max(1, expiryDate.diff(now, "days"));

    const yearsFloat = totalMonthsFloat / 12;
    const fullYears = Math.floor(yearsFloat);
    const remainder = yearsFloat - fullYears;

    // Handle years > 1.5
    if (yearsFloat >= 1.5) {
      const roundedYears = remainder > 0.5 ? fullYears + 1 : fullYears;
      return `Expires in ${roundedYears} year${roundedYears > 1 ? "s" : ""}`;
    }

    // Handle 1 – 1.5 years
    if (yearsFloat >= 1) {
      return "Expires in 1 year";
    }

    // Handle 1–11 months (never fall back to days here)
    const wholeMonths = expiryDate.diff(now, "months");
    if (wholeMonths >= 1) {
      return `Expires in ${wholeMonths} month${wholeMonths > 1 ? "s" : ""}`;
    }

    // Handle < 1 month: days
    return `Expires in ${totalDays} day${totalDays > 1 ? "s" : ""}`;
  };

  const getCompanyCardExpiryStatus = (
    rawDate: string
  ): COMPANY_CARD_EXPIRY_STATUS => {
    const now = moment.utc().startOf("day");
    const expiryDate = moment.utc(rawDate, "YYYY-MM-DD").startOf("day");

    if (now.isAfter(expiryDate)) {
      return COMPANY_CARD_EXPIRY_STATUS.Critical;
    }

    const monthsLeft = expiryDate.diff(now, "months", true); // true for float result

    if (monthsLeft > 6) {
      return COMPANY_CARD_EXPIRY_STATUS.Success;
    } else if (monthsLeft > 3) {
      return COMPANY_CARD_EXPIRY_STATUS.Info;
    } else {
      return COMPANY_CARD_EXPIRY_STATUS.Warning;
    }
  };

  // Define the mapping between COMPANY_CARD_EXPIRY_STATUS and BadgeColors
  const COMPANY_CARD_EXPIRY_STATUS_TO_BADGE_COLORS: Record<
    COMPANY_CARD_EXPIRY_STATUS,
    BadgeColors
  > = {
    [COMPANY_CARD_EXPIRY_STATUS.Critical]: "critical",
    [COMPANY_CARD_EXPIRY_STATUS.Success]: "success",
    [COMPANY_CARD_EXPIRY_STATUS.Info]: "info",
    [COMPANY_CARD_EXPIRY_STATUS.Warning]: "warning"
  };

  // compare the status with the badge color
  const isEqual = (
    status: COMPANY_CARD_EXPIRY_STATUS,
    badgeColor: BadgeColors
  ): boolean => {
    return COMPANY_CARD_EXPIRY_STATUS_TO_BADGE_COLORS[status] === badgeColor;
  };

  return { getExpiryLabel, getCompanyCardExpiryStatus, isEqual };
};
